import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import axios from "axios";
import { fmDataContext } from "../../Context/FmData";
import Joi from "joi";
import { useParams } from "react-router-dom";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import LoadingCircle from "../LoadingCircle/LoadingCircle";
import { authenticationContext } from "../../Context/AuthContext";
import userCan from "../../helpers/userCan";
import { toast } from "react-toastify";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import jwtDecode from "jwt-decode";

const UpdatePageHeader = () => {
  let [mounted, setMounted] = useState(false);

  let { pageHeaderId } = useParams();
  let { baseUrl, navigate, showData, globalCrumbs, setGlobalCrumbs } = useContext(fmDataContext);
  let { user } = useContext(authenticationContext);

  let [currentPageHeader, setCurrentPageHeader] = useState({});
  let [isPageHeaderLoading, setIsPageHeaderLoading] = useState(true);

  let [selectedImage, setSelectedImage] = useState({});

  let [joiErrorsList, setJoiErrorsList] = useState([]);
  let [imageInputError, setImageInputError] = useState(null);

  let [pageHeaderData, setPageHeaderData] = useState({
    page_title: "",
    image: selectedImage,
    modify_user_id: null,
  });

  let [isUpdateLoading, setIsUpdateLoading] = useState(false);

  let [updateResponse, setUpdateResponse] = useState({});

  const animatedComponents = makeAnimated();

  let pageTitleRef = useRef();

  let [selectedPageTitle, setSelectedPageTitle] = useState(null);

  let [navigateTimeOut, setNavigateTimeOut] = useState("");

  let userToken = jwtDecode(localStorage.getItem("userToken")).accessToken;

  const getPageHeader = (reqMethod, pathName, setLoading = setIsPageHeaderLoading, headers) => {
    showData(reqMethod, pathName, setLoading, headers)
      .then((response) => {
        // ------------------ REMOVE BEFORE PRODUCTION --------------------------------
        // if (response.data.data.image.includes("via.placeholder.com")) {
        //   response.data.data.image = response.data.data.image.replace("http://127.0.0.1:8000/storage/", "");
        // }
        // ------------------ REMOVE BEFORE PRODUCTION --------------------------------
        let pageHeaderResponse = response.data.data;

        setCurrentPageHeader(pageHeaderResponse);
        setPageHeaderData({
          page_title: pageHeaderResponse.page_title ? pageHeaderResponse.page_title : "",
          image: selectedImage,
          modify_user_id: null,
        });

        setLoading(false);
      })
      .catch((error) => {
        navigate("/not-found");
      });
  };

  const handlePageTitle = () => {
    const pageHeader = { ...pageHeaderData };
    pageHeader.page_title = selectedPageTitle !== null && selectedPageTitle.value !== null ? selectedPageTitle.value : "";
    setPageHeaderData(pageHeader);
  };

  const handleImageSelect = (e) => {
    setSelectedImage(e.target.files[0]);
  };

  const updateItem = async (e) => {
    e.preventDefault();
    if (userCan("fm_page_header_update")) {
      pageHeaderData.modify_user_id = user.currentUser._id;

      const validationResult = validateUpdatePageHeaderForm();
      let imageError = null;
      setUpdateResponse({});

      if (selectedImage && selectedImage.name) {
        pageHeaderData.image = selectedImage;
        if (!["image/png", "image/jpg", "image/jpeg", "image/gif"].includes(pageHeaderData.image.type)) {
          imageError = "File must be an Image and from These extensions (png, jpg, jpeg, gif)";
        } else if (pageHeaderData.image.size > 5 * 1024 * 1024) {
          imageError = "Image Size Must Be Less Than 5 MB";
        }
      } else {
        pageHeaderData.image = null;
      }

      if (validationResult.error || imageError) {
        validationResult.error ? setJoiErrorsList(validationResult.error.details) : setJoiErrorsList([]);
        setImageInputError(imageError);
      } else {
        setIsUpdateLoading(true);
        setImageInputError(null);
        setJoiErrorsList([]);

        await axios({
          method: "POST",
          url: `${baseUrl}fm-page-headers/update/${currentPageHeader.key}`,
          data: pageHeaderData,
          params: {
            _method: "PUT",
          },
          headers: {
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
            "fm-api-secret-key": process.env.REACT_APP_FM_API_SECRET_KEY,
            Authorization: `Bearer ${user.accessToken}`,
          },
        })
          .then((response) => {
            setUpdateResponse(response.data);
            setIsUpdateLoading(false);

            if (response.data.msg && response.data.msg.includes("successfully")) {
              let timeOut = setTimeout(() => {
                setUpdateResponse({});
                navigate(`/fm-page-headers/${currentPageHeader.key}?archived=0`);
              }, 4000);
              setNavigateTimeOut(timeOut);
              setPageHeaderData({
                page_title: "",
                image: {},
                modify_user_id: null,
              });
              setSelectedImage(null);
              e.target.reset();
              pageTitleRef.current.clearValue();
              toast(`${response.data.msg}, navigating...`, {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                progressStyle: { backgroundColor: "#fdb915" },
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                progress: undefined,
                theme: "dark",
                containerId: "update_done_toast",
              });
            } else if (response.data.msg && response.data.msg.includes("failed")) {
              toast(response.data.msg, {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                progressStyle: { backgroundColor: "#fdb915" },
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                progress: undefined,
                theme: "dark",
              });
            }
          })
          .catch((errors) => {
            navigate("/not-found");
          });
      }
    } else {
      navigate("/not-found");
    }
  };

  const validateUpdatePageHeaderForm = () => {
    const scheme = Joi.object({
      page_title: Joi.string().min(1).max(255).required(),
      image: Joi.any(),
      modify_user_id: Joi.number(),
    });
    return scheme.validate(pageHeaderData, { abortEarly: false });
  };

  useEffect(() => {
    getPageHeader("GET", `${baseUrl}fm-page-header/${pageHeaderId}`, setIsPageHeaderLoading, {
      Accept: "application/json",
      "fm-api-secret-key": process.env.REACT_APP_FM_API_SECRET_KEY,
      Authorization: `Bearer ${userToken}`,
    });

    setMounted(true);
  }, []);

  useEffect(() => {
    return () => {
      if (mounted) {
        clearTimeout(navigateTimeOut);
      }
    };
  }, [navigateTimeOut]);

  useEffect(() => {
    if (mounted && currentPageHeader) {
      let globalCrumbsUpdated = globalCrumbs.map((crumb) => {
        if (crumb.name === pageHeaderId.toUpperCase()) {
          crumb.name = `${currentPageHeader.page_title} header image`.toUpperCase();
        }
        return crumb;
      });
      setGlobalCrumbs(globalCrumbsUpdated);
      if (pageTitleRef.current && currentPageHeader.page_title) {
        pageTitleRef.current.setValue(currentPageHeader.page_title ? { value: currentPageHeader.page_title, label: `${currentPageHeader.page_title.charAt(0).toUpperCase()}${currentPageHeader.page_title.slice(1)}` } : null);
      }
    }
  }, [currentPageHeader]);

  useEffect(() => {
    handlePageTitle();
  }, [selectedPageTitle]);

  return (
    <>
      {isPageHeaderLoading ? (
        <LoadingScreen />
      ) : (
        <div className="container position-relative pt-3 pt-md-4">
          <div className="mb-3 mb-md-4">
            <h1 className="fu-text-3-rem fu-text-3-rem-md fu-text-md-2-rem fu-text-sm-2-rem fu-text-1-8-rem-xs fu-text-18-px-xxs fu-text-16-px-xxs fu-fw-800 fu-text-white mb-0">Update Page Header</h1>
          </div>
          <div className="py-2 mb-4 iu-row-heading create-submit-row">
            <h2 className="mb-0 row-title">Edit ( {currentPageHeader.page_title} ) page header data</h2>
          </div>
          <div className="row">
            <div>
              <div>
                <Form onSubmit={updateItem}>
                  <Row>
                    <Col xl={6}>
                      <div className="mb-3">
                        <div className="fu-text-light-gray w-fit-content mb-2" data-title="required">
                          Page Title
                          <span className="required-input mx-1" data-title="required">
                            *
                          </span>
                        </div>
                        <div className="select-options">
                          <Select
                            ref={pageTitleRef}
                            classNames={{
                              control: () => "select-filtration",
                              option: ({ isSelected, innerProps }) => (isSelected ? (innerProps.style = { backgroundColor: "#fdb915", color: "#191919" }) : (innerProps.style = { backgroundColor: "#ffffff" })),
                            }}
                            placeholder="Select Page Title...."
                            name="verified"
                            onChange={setSelectedPageTitle}
                            closeMenuOnSelect={true}
                            components={animatedComponents}
                            isClearable
                            options={[
                              { value: "labels", label: "Labels" },
                              { value: "artists", label: "Artists" },
                              { value: "music", label: "Music" },
                              { value: "videos", label: "Videos" },
                              { value: "contact", label: "Contact" },
                              { value: "news", label: "News" },
                              { value: "partnerships", label: "Partnerships" },
                              { value: "about", label: "About" },
                              { value: "privacy-policy", label: "Privacy-Policy" },
                              { value: "demo-drop", label: "Demo-Drop" },
                            ]}
                          />
                        </div>
                        {joiErrorsList.map((error, index) =>
                          error.path[0] === "page_title" ? (
                            <div key={index} className="alert alert-danger py-1 px-2 mt-2 mb-0">
                              {error.message.replace("page_title", "Page Title")}
                            </div>
                          ) : (
                            ""
                          )
                        )}
                      </div>
                    </Col>
                    <Col xl={6}>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <Form.Group className="mb-3" controlId="pageHeaderImage">
                            <Form.Label className="fu-text-light-gray" data-title="required">
                              Page Header Image
                              <span className="required-input mx-1" data-title="required">
                                *
                              </span>
                            </Form.Label>
                            <Form.Control type="file" placeholder="Choose Page Header Image" className="fu-input" name="image" onInput={handleImageSelect} />

                            {imageInputError !== null ? <div className="alert alert-danger py-1 px-2 mt-2 mb-0">{imageInputError}</div> : ""}
                          </Form.Group>
                        </div>
                        <div className="ms-2 align-self-center">
                          <div className="rectangle-table-item-img-container d-flex align-items-center">
                            <img src={currentPageHeader.image} alt="Page Header" className="w-100" />
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  {/* ------------------------------------ */}

                  {updateResponse.errors
                    ? Object.keys(updateResponse.errors).map((key) =>
                        updateResponse.errors[key].map((error, index) => (
                          <div key={index} className="alert alert-danger py-1 px-2 mt-2 mb-0">
                            {error}
                          </div>
                        ))
                      )
                    : ""}

                  <div className="d-flex align-items-center mt-4">
                    <div>
                      <Button className={`fu-btn fu-btn-gray ${isUpdateLoading ? "disabled" : ""}`} type="submit" disabled={isUpdateLoading ? true : false}>
                        <span className="btn-bg"></span>
                        Submit<i className="bi bi-send ms-2"></i>
                      </Button>
                    </div>
                    {isUpdateLoading ? (
                      <div className="position-relative ms-3">
                        <LoadingCircle circleCont={35} icon={30} loader={35} dot={35} />
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpdatePageHeader;
